import {
  faBell,
  faEnvelope,
  faExternalLink,
  faMessage,
  faPhone
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import { memo, useContext, useMemo } from 'react';

import { ActivityLogEvent } from '@/constants/activityLogEvent';
import { Color, ColorBorderLight, ColorVariantsLight } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import PersonContext from '@/pages/Person/PersonContext';
import ConversationStatusAndTags from '@/pages/Timeline/components/ConversationStatusAndTags/ConversationStatusAndTags';
import EventTimestamp from '@/pages/Timeline/components/EventTimestamp';
import { Channel } from '@/pages/Timeline/components/MessageEvent/types';
import messageEventTitle from '@/pages/Timeline/components/MessageEvent/utils/messageEventTitle';
import TimelineHeader from '@/pages/Timeline/components/TimelineHeader/TimelineHeader';
import { TimelineSubcomponentProps } from '@/pages/Timeline/types';
import MessageRepository from '@/repositories/MessageRepository';
import Button from '@/ui/components/Button/Button';
import Card from '@/ui/components/Card';
import Spinner from '@/ui/components/Spinner';
import formatStaffName from '@/utils/formatStaffName';
import { kustomerEventLink } from '@/utils/kustomerLinks';
import snakeCaseToTitleCase from '@/utils/snakeToTitleCase';

// const logdataExample = {
//   backfill: false,
//   message: {
//     app: 'one_signal',
//     auto: true,
//     channel: 'push',
//     direction: 'out',
//     sentDatetime: '2023-06-22T15:00:14.652+00:00',
//   },
//   message_body: "Your appointment is starting soon.",
//   message_title: 'Some title here.'
//   sent_at: '2023-06-22T15:00:14.652+00:00'
// };

const MessageEvent = ({ activityLog }: TimelineSubcomponentProps): JSX.Element => {
  const { action, actor, data: activityLogData, object_id } = activityLog;
  const { addMessage, host } = useContext(AppContext);
  const {
    person: { kustomer_id: personKustomerId }
  } = useContext(PersonContext);
  const messageRepository = MessageRepository();
  const isIterable = activityLogData?.message?.app === 'iterable';

  const {
    data: message,
    isFetching,
    refetch: getMessage
  } = useAPI({
    queryKey: ['getMessage', object_id],
    queryFn: async () => {
      return await messageRepository.getMessage(object_id);
    },
    enabled: false
  });

  const { isFetching: isFetchingConversationId, refetch: getKustomerConversationId } = useAPI({
    queryKey: ['getMessageKustomerConversationId', object_id],
    queryFn: async () => {
      return await messageRepository.kustomerConversationId(object_id);
    },
    enabled: false
  });

  const title = useMemo(
    () => messageEventTitle(action, activityLogData),
    [action, activityLogData]
  );

  const subtitle = useMemo(() => {
    return isIterable ? activityLogData?.iterable_campaign_name : activityLogData?.message_title;
  }, [activityLogData?.iterable_campaign_name, activityLogData?.message_title, isIterable]);

  const messageStyle = useMemo(() => {
    return action &&
      [
        ActivityLogEvent.inbound_message_created.toString(),
        ActivityLogEvent.INBOUND_MESSAGE_CREATED_EVENT.toString()
      ].includes(action)
      ? `float-right ${ColorVariantsLight[Color.EMERALD]} ${ColorBorderLight[Color.EMERALD]}`
      : `${ColorVariantsLight[Color.GRAY]} ${ColorBorderLight[Color.GRAY]}`;
  }, [action]);

  const iconColorClassName = useMemo(() => {
    return activityLogData?.message?.channel === Channel.SMS
      ? isIterable
        ? 'text-purple-700'
        : 'text-green-500'
      : 'text-black';
  }, [activityLogData?.message?.channel, isIterable]);

  const icon = useMemo(() => {
    switch (activityLogData?.message?.channel) {
      case Channel.SMS:
        return faMessage;
      case Channel.PUSH:
        return faBell;
      case Channel.VOICE:
        return faPhone;
      case Channel.EMAIL:
        return faEnvelope;
      default:
        return faMessage;
    }
  }, [activityLogData?.message?.channel]);

  const handleViewInKustomerClick = async () => {
    const response = await getKustomerConversationId();

    if (response?.data) {
      const { kustomer_id } = response.data;

      if (kustomer_id) {
        window.open(
          kustomerEventLink({
            host,
            kustomerId: kustomer_id,
            personKustomerId: personKustomerId as string // value asserted below
          }),
          '_blank'
        );
      } else {
        addMessage({ level: 'error', text: 'No Kustomer Conversation Id found for link.' });
      }
    }
  };

  const messageDetails = (): JSX.Element | null => {
    if (message) {
      return <ConversationStatusAndTags conversation={message.conversation} />;
    }

    return null;
  };

  return (
    <div>
      <Card>
        <TimelineHeader
          icon={icon}
          iconClassName={iconColorClassName}
          subtitle={subtitle}
          title={
            <span className="capitalize">
              {title}{' '}
              {personKustomerId && (
                <button
                  aria-label="View in Kustomer"
                  className="ml-2"
                  disabled={isFetchingConversationId}
                  onClick={handleViewInKustomerClick}
                  type="button">
                  {isFetchingConversationId ? (
                    <span className="font-normal text-xs">
                      <Spinner />
                    </span>
                  ) : (
                    <FontAwesomeIcon icon={faExternalLink} className="text-gray-600" />
                  )}
                </button>
              )}
            </span>
          }
        />

        {activityLogData?.message_body && (
          <div className={`w-3/4 my-1 p-2 rounded-lg ${messageStyle}`}>
            {activityLogData.message_body}
          </div>
        )}

        <div className="clear-both">
          {!message && (
            <Button
              color={Color.WHITE}
              loading={isFetching}
              loadingWithSpinner={false}
              onClick={() => getMessage()}
              size="xs">
              Click for Status &amp; Tags
            </Button>
          )}

          {messageDetails()}
        </div>
      </Card>

      <div className="flex justify-between text-sm text-gray-600 px-2 -mt-1.5">
        <EventTimestamp time={activityLog.timestamp} />

        <div data-testid="actor">
          {actor
            ? actor.first_name || actor.last_name
              ? formatStaffName({ staff: actor })
              : 'Unknown Name'
            : activityLogData?.message?.app
              ? snakeCaseToTitleCase(activityLogData.message.app)
              : ''}
        </div>
      </div>
    </div>
  );
};

export default memo(MessageEvent);
